import {
  notifyCreditExhaustion,
} from "@tribunalea/ui/credit-exhaustion";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:4000";

export async function apiRequest(path, { token, ...options } = {}) {
  const hasFormData = typeof FormData !== "undefined"
    && options.body instanceof FormData;
  const response = await fetch(`${API_URL}${path}`, {
    ...options,
    credentials: "include",
    headers: {
      ...(!hasFormData && options.body
        ? { "content-type": "application/json" }
        : {}),
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });

  if (response.status === 204) return null;
  const payload = await response.json().catch(() => ({}));
  notifyCreditExhaustion(payload);
  if (!response.ok) {
    const error = new Error(payload.error || "No pudimos completar la operación.");
    error.code = payload.code;
    error.issues = payload.issues;
    throw error;
  }
  return payload.data;
}

export function createApi(tokenGetter) {
  const request = async (path, options) => {
    const token = tokenGetter ? await tokenGetter() : null;
    return apiRequest(path, { token, ...options });
  };
  const requestDocument = async (input) => {
    const token = tokenGetter ? await tokenGetter() : null;
    const response = await fetch(
      `${API_URL}/api/expedientes/view/actuaciones/document`,
      {
        method: "POST",
        credentials: "include",
        headers: {
          "content-type": "application/json",
          ...(token ? { authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(input),
      },
    );
    const contentType = response.headers.get("content-type") || "";
    if (response.ok && contentType.includes("application/pdf")) {
      const blob = await response.blob();
      return {
        status: "pdf",
        blob,
        fileName: decodeDocumentName(
          response.headers.get("content-disposition"),
        ),
      };
    }
    const payload = await response.json().catch(() => ({}));
    notifyCreditExhaustion(payload);
    if (!response.ok) {
      const error = new Error(
        payload.error || "No pudimos consultar el PDF.",
      );
      error.code = payload.code;
      throw error;
    }
    return payload.data;
  };
  const requestBlob = async (path, fallbackName, options = {}) => {
    const token = tokenGetter ? await tokenGetter() : null;
    const hasFormData = typeof FormData !== "undefined"
      && options.body instanceof FormData;
    const response = await fetch(`${API_URL}${path}`, {
      ...options,
      credentials: "include",
      headers: {
        ...(!hasFormData && options.body
          ? { "content-type": "application/json" }
          : {}),
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...options.headers,
      },
    });
    if (!response.ok) {
      const payload = await response.json().catch(() => ({}));
      notifyCreditExhaustion(payload);
      throw new Error(
        payload.error || "No pudimos descargar el documento.",
      );
    }
    return {
      blob: await response.blob(),
      fileName: decodeDocumentName(
        response.headers.get("content-disposition"),
        fallbackName,
      ),
    };
  };

  return {
    listRules: () => request("/api/notifications"),
    getRule: (id) => request(`/api/notifications/${id}`),
    createRule: (input) =>
      request("/api/notifications", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    updateRule: (id, input) =>
      request(`/api/notifications/${id}`, {
        method: "PATCH",
        body: JSON.stringify(input),
      }),
    deleteRule: (id) =>
      request(`/api/notifications/${id}`, { method: "DELETE" }),
    judiciaryCatalog: () => request("/api/judiciary/catalog"),
    dailyListFilters: () =>
      request("/api/judiciary/daily-list-filters"),
    createDailyListFilter: (input) =>
      request("/api/judiciary/daily-list-filters", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    updateDailyListFilter: (id, input) =>
      request(`/api/judiciary/daily-list-filters/${id}`, {
        method: "PATCH",
        body: JSON.stringify(input),
      }),
    deleteDailyListFilter: (id) =>
      request(`/api/judiciary/daily-list-filters/${id}`, {
        method: "DELETE",
      }),
    dailyJudiciaryList: ({
      circunscripcionId,
      typeId,
      courtId,
      from,
      to,
    }) =>
      request(
        `/api/judiciary/daily-list?circunscripcionId=${encodeURIComponent(
          circunscripcionId,
        )}&typeId=${encodeURIComponent(typeId)}&courtId=${encodeURIComponent(
          courtId,
        )}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
      ),
    deliveries: () => request("/api/deliveries"),
    profile: () => request("/api/profile"),
    updateProfile: (input) =>
      request("/api/profile", {
        method: "PUT",
        body: JSON.stringify(input),
      }),
    team: () => request("/api/profile/team"),
    searchProfessionals: (query, {
      limit = 20,
      jurisdiction = "all",
    } = {}) => {
      const parameters = new URLSearchParams({
        query,
        limit: String(limit),
        jurisdiction,
      });
      return request(`/api/professionals/search?${parameters}`);
    },
    listPraxisLawyers: () => request("/api/praxis/lawyers"),
    addPraxisLawyer: (input) =>
      request("/api/praxis/lawyers", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    removePraxisLawyer: (lawyerId) =>
      request(`/api/praxis/lawyers/${lawyerId}`, {
        method: "DELETE",
      }),
    runDevelopmentNotificationScrape: () =>
      request("/api/development/notification-scrape/run", {
        method: "POST",
      }),
    listExpedientes: ({ archived = false } = {}) =>
      request(`/api/expedientes?archived=${archived}`),
    listJustinianoConversations: ({
      limit = 30,
      cursor,
      query,
      assignedOnly = false,
      contextType,
      contextId,
    } = {}) => {
      const parameters = new URLSearchParams({ limit: String(limit) });
      if (cursor) parameters.set("cursor", cursor);
      if (query) parameters.set("query", query);
      if (assignedOnly) parameters.set("assignedOnly", "true");
      if (contextType) parameters.set("contextType", contextType);
      if (contextId) parameters.set("contextId", contextId);
      return request(
        `/api/mentis/v1/apps/praxis/conversations/history?${parameters}`,
      );
    },
    searchExpedientes: ({ query, archived = false, limit = 10 }) =>
      request(
        `/api/expedientes/search?q=${encodeURIComponent(query)}&archived=${archived}&limit=${limit}`,
      ),
    lookupJudiciaryExpedientes: ({ query, limit = 10, page = 1 }) =>
      request(
        `/api/expedientes/lookup?q=${encodeURIComponent(query)}&limit=${limit}&page=${page}`,
      ),
    getExpedienteFromSource: (identifier) =>
      request(
        `/api/expedientes/source-view?identifier=${encodeURIComponent(identifier)}`,
      ),
    resolveExpedienteExternalId: (externalId) =>
      request(`/api/expedientes/external/${encodeURIComponent(externalId)}`),
    addExpediente: (cuij) =>
      request("/api/expedientes", {
        method: "POST",
        body: JSON.stringify({ cuij }),
      }),
    getExpediente: (cuij, { includeTimeline = false } = {}) =>
      request(
        `/api/expedientes/view?cuij=${encodeURIComponent(
          cuij,
        )}&includeTimeline=${includeTimeline}`,
      ),
    getExpedienteTimeline: (cuij) =>
      request(
        `/api/expedientes/view/timeline?cuij=${encodeURIComponent(cuij)}`,
      ),
    getExpedienteProcedureState: (cuij) =>
      request(
        `/api/expedientes/view/procedure-state?cuij=${encodeURIComponent(cuij)}`,
      ),
    getExpedienteIntelligence: (cuij) =>
      request(
        `/api/expedientes/view/intelligence?cuij=${encodeURIComponent(cuij)}`,
      ),
    retryExpedienteIntelligence: (cuij) =>
      request("/api/expedientes/view/intelligence/retry", {
        method: "POST",
        body: JSON.stringify({ cuij }),
      }),
    setExpedienteInconsistencyAcknowledgment: (
      cuij,
      inconsistencyId,
      acknowledged,
    ) =>
      request(
        "/api/expedientes/view/intelligence/inconsistency-acknowledgment",
        {
          method: "PUT",
          body: JSON.stringify({ cuij, inconsistencyId, acknowledged }),
        },
      ),
    setExpedienteDeadlineOverride: (cuij, deadlineId, status) =>
      request("/api/expedientes/view/procedure-state/deadline-override", {
        method: "PUT",
        body: JSON.stringify({ cuij, deadlineId, status }),
      }),
    listWorkspaceDocuments: ({ productId, contextType, contextId }) => {
      const parameters = new URLSearchParams();
      if (productId) parameters.set("productId", productId);
      if (contextType) parameters.set("contextType", contextType);
      if (contextId) parameters.set("contextId", contextId);
      return request(`/api/workspace-documents?${parameters}`);
    },
    createWorkspaceDocument: (input) =>
      request("/api/workspace-documents", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    getWorkspaceDocument: (id) =>
      request(`/api/workspace-documents/${id}`),
    updateWorkspaceDocument: (id, input) =>
      request(`/api/workspace-documents/${id}`, {
        method: "PATCH",
        body: JSON.stringify(input),
      }),
    deleteWorkspaceDocument: (id) =>
      request(`/api/workspace-documents/${id}`, { method: "DELETE" }),
    prepareWorkspaceDocumentContext: (id, { force = false } = {}) =>
      request(`/api/workspace-documents/${id}/writing-context`, {
        method: "POST",
        body: JSON.stringify({ force }),
      }),
    suggestWorkspaceDocument: (id, input) =>
      request(`/api/workspace-documents/${id}/suggestions`, {
        method: "POST",
        body: JSON.stringify(input),
      }),
    editWorkspaceDocumentWithAi: (id, input) =>
      request(`/api/workspace-documents/${id}/ai-edit`, {
        method: "POST",
        body: JSON.stringify(input),
      }),
    exportWorkspaceDocument: (id) =>
      requestBlob(
        `/api/workspace-documents/${id}/export/docx`,
        "escrito.docx",
      ),
    refreshExpediente: (
      cuij,
      { includeTimeline = false, force = true } = {},
    ) =>
      request("/api/expedientes/view/refresh", {
        method: "POST",
        body: JSON.stringify({ cuij, includeTimeline, force }),
      }),
    retryExpedienteDetails: (cuij, { includeTimeline = false } = {}) =>
      request("/api/expedientes/view/details/retry", {
        method: "POST",
        body: JSON.stringify({ cuij, includeTimeline }),
      }),
    archiveExpediente: (cuij) =>
      request("/api/expedientes/view/archive", {
        method: "POST",
        body: JSON.stringify({ cuij }),
      }),
    restoreExpediente: (cuij) =>
      request("/api/expedientes/view/restore", {
        method: "POST",
        body: JSON.stringify({ cuij }),
      }),
    removeExpediente: (cuij) =>
      request("/api/expedientes/view", {
        method: "DELETE",
        body: JSON.stringify({ cuij }),
      }),
    listFollowUps: () => request("/api/follow-ups"),
    addFollowUps: (cuijs) =>
      request("/api/follow-ups", {
        method: "POST",
        body: JSON.stringify({ cuijs }),
      }),
    addFollowUpReminder: (followUpId, input) =>
      request(`/api/follow-ups/${followUpId}/reminders`, {
        method: "POST",
        body: JSON.stringify(input),
      }),
    addExpedienteManualReminder: (cuij, input) =>
      request("/api/follow-ups/expediente/reminders", {
        method: "POST",
        body: JSON.stringify({ cuij, ...input }),
      }),
    dismissFollowUpReminder: (followUpId, reminderId) =>
      request(
        `/api/follow-ups/${followUpId}/reminders/${reminderId}`,
        { method: "DELETE" },
      ),
    setFollowUpReminderStatus: (followUpId, reminderId, status) =>
      request(`/api/follow-ups/${followUpId}/reminders/${reminderId}`, {
        method: "PATCH",
        body: JSON.stringify({ status }),
      }),
    removeFollowUp: (followUpId) =>
      request(`/api/follow-ups/${followUpId}`, { method: "DELETE" }),
    listWorkflowTemplates: () =>
      request("/api/praxis/workflow-templates"),
    getWorkflowTemplate: (templateId) =>
      request(`/api/praxis/workflow-templates/${templateId}`),
    createWorkflowTemplate: (input) =>
      request("/api/praxis/workflow-templates", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    updateWorkflowTemplate: (templateId, input) =>
      request(`/api/praxis/workflow-templates/${templateId}`, {
        method: "PUT",
        body: JSON.stringify(input),
      }),
    archiveWorkflowTemplate: (templateId) =>
      request(`/api/praxis/workflow-templates/${templateId}`, {
        method: "DELETE",
      }),
    importWorkflowTemplate: (file) => {
      const form = new FormData();
      form.append("workflow", file);
      return request("/api/praxis/workflow-templates/import", {
        method: "POST",
        body: form,
      });
    },
    exportWorkflowTemplate: (templateId) =>
      requestBlob(
        `/api/praxis/workflow-templates/${templateId}/export`,
        "flujo.tribunalea.json",
      ),
    listWorkflowRecords: (
      templateId,
      { page = 1, limit = 25, filters = [] } = {},
    ) => {
      const parameters = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });
      if (filters.length) parameters.set("filters", JSON.stringify(filters));
      return request(
        `/api/praxis/workflow-templates/${templateId}/records?${parameters}`,
      );
    },
    exportWorkflowRecords: (templateId, filters = []) =>
      requestBlob(
        `/api/praxis/workflow-templates/${templateId}/records/export`,
        "registros.xlsx",
        {
          method: "POST",
          body: JSON.stringify({ filters }),
        },
      ),
    getWorkflowRecord: (templateId, recordId) =>
      request(
        `/api/praxis/workflow-templates/${templateId}/records/${recordId}`,
      ),
    exportWorkflowRecordWord: (templateId, recordId) =>
      requestBlob(
        `/api/praxis/workflow-templates/${templateId}/records/${recordId}/export/word`,
        "registro.docx",
      ),
    exportWorkflowRecordPdf: (templateId, recordId) =>
      requestBlob(
        `/api/praxis/workflow-templates/${templateId}/records/${recordId}/export/pdf`,
        "registro.pdf",
      ),
    getWorkflowRecordText: (templateId, recordId) =>
      request(
        `/api/praxis/workflow-templates/${templateId}/records/${recordId}/export/text`,
      ),
    createWorkflowRecord: (templateId, input) =>
      request(`/api/praxis/workflow-templates/${templateId}/records`, {
        method: "POST",
        body: JSON.stringify(input),
      }),
    resolveWorkflowRecordValues: (templateId, input) =>
      request(
        `/api/praxis/workflow-templates/${templateId}/records/resolve`,
        {
          method: "POST",
          body: JSON.stringify(input),
        },
      ),
    updateWorkflowRecord: (templateId, recordId, input) =>
      request(
        `/api/praxis/workflow-templates/${templateId}/records/${recordId}`,
        {
          method: "PUT",
          body: JSON.stringify(input),
        },
      ),
    deleteWorkflowRecord: (templateId, recordId) =>
      request(
        `/api/praxis/workflow-templates/${templateId}/records/${recordId}`,
        { method: "DELETE" },
      ),
    listWorkflowInterestDocuments: (templateId, fieldId, cuij) => {
      const parameters = new URLSearchParams({ fieldId, cuij });
      return request(
        `/api/praxis/workflow-templates/${templateId}/interest/documents?${parameters}`,
      );
    },
    createWorkflowInterestAnalysis: (templateId, input) => {
      const form = new FormData();
      form.append("fieldId", input.fieldId);
      form.append("documents", JSON.stringify(input.documents || []));
      form.append("fallbackDate", input.fallbackDate);
      (input.files || []).forEach((file) => form.append("files", file));
      return request(
        `/api/praxis/workflow-templates/${templateId}/interest/analyses`,
        {
          method: "POST",
          body: form,
        },
      );
    },
    getWorkflowInterestCalculation: (templateId, fieldId, calculationId) => {
      const parameters = new URLSearchParams({ fieldId });
      return request(
        `/api/praxis/workflow-templates/${templateId}/interest/calculations/${calculationId}?${parameters}`,
      );
    },
    retryWorkflowInterestAnalysis: (
      templateId,
      calculationId,
      { fieldId, clarification },
    ) => request(
      `/api/praxis/workflow-templates/${templateId}/interest/calculations/${calculationId}/retry-analysis`,
      {
        method: "POST",
        body: JSON.stringify({ fieldId, clarification }),
      },
    ),
    calculateWorkflowInterest: (templateId, calculationId, input) =>
      request(
        `/api/praxis/workflow-templates/${templateId}/interest/calculations/${calculationId}/calculate`,
        {
          method: "POST",
          body: JSON.stringify(input),
        },
      ),
    listIntegrations: () => request("/api/integrations"),
    getPjmNotificationsIntegration: () =>
      request("/api/integrations/pjm-notifications"),
    configurePjmNotificationsIntegration: (input) =>
      request("/api/integrations/pjm-notifications", {
        method: "PUT",
        body: JSON.stringify(input),
      }),
    syncPjmNotificationsIntegration: () =>
      request("/api/integrations/pjm-notifications/sync", {
        method: "POST",
      }),
    removePjmNotificationsIntegration: () =>
      request("/api/integrations/pjm-notifications", {
        method: "DELETE",
      }),
    getPjnScwIntegration: () =>
      request("/api/integrations/pjn-scw"),
    configurePjnScwIntegration: (input) =>
      request("/api/integrations/pjn-scw", {
        method: "PUT",
        body: JSON.stringify(input),
      }),
    removePjnScwIntegration: () =>
      request("/api/integrations/pjn-scw", {
        method: "DELETE",
      }),
    actuacionDocument: ({ cuij, actId, attachmentId }) =>
      requestDocument({
        cuij,
        actId,
        ...(attachmentId ? { attachmentId } : {}),
      }),
  };
}

function decodeDocumentName(contentDisposition, fallbackName = "actuacion.pdf") {
  const encoded = contentDisposition?.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  if (!encoded) return fallbackName;
  try {
    return decodeURIComponent(encoded);
  } catch {
    return fallbackName;
  }
}
